/**
 * Pure rules behind the submission review: which answers changed since the
 * previous revision, which decisions carry forward, which uploads the AI check
 * flagged, the row filters, and the note drafted for a revision request. No
 * React, so the rules are unit-testable.
 */
import type {
  OutgoingIntakeAnswer,
  OutgoingIntakeReviewDecision,
  OutgoingIntakeSubmission
} from "@/api/types/outgoingIntake";

export type Decision = "pending" | "accepted" | "rejected";

export type DecisionState = {
  status: Decision;
  note?: string;
  /** Accepted on the previous revision and unchanged since. */
  carried?: boolean;
};

const text = (v: unknown): string => (typeof v === "string" ? v.trim() : "");

export const decisionKey = (a: {
  question_id: string;
  repeatable_group_id?: string | null;
  repeatable_group_instance_id?: string | null;
}): string =>
  [
    text(a.question_id),
    text(a.repeatable_group_id),
    text(a.repeatable_group_instance_id)
  ].join("|");

const stable = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== null && v !== undefined && v !== "")
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => [k, stable(v)]);
    return Object.fromEntries(entries);
  }
  return typeof value === "string" ? value.trim() : value;
};

/** What makes two answers the same: the value and which files are attached. */
export const answerFingerprint = (
  answer: OutgoingIntakeAnswer | null | undefined
): string => {
  if (!answer) return "";
  const files = (answer.files ?? []).map((f) => f.sha256 || f.file_id).sort();
  const value = stable(answer.value);
  const empty =
    value === undefined ||
    value === null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0);
  if (empty && files.length === 0) return "";
  return JSON.stringify({ value: empty ? null : value, files });
};

export const answersByKey = (
  submission: OutgoingIntakeSubmission | null | undefined
): Map<string, OutgoingIntakeAnswer> => {
  const out = new Map<string, OutgoingIntakeAnswer>();
  for (const a of submission?.answers ?? []) out.set(decisionKey(a), a);
  return out;
};

/** Keys whose answer differs from the previous revision (null when there is none). */
export const changedSince = (
  current: OutgoingIntakeSubmission,
  previous: OutgoingIntakeSubmission | null | undefined
): Set<string> | null => {
  if (!previous) return null;
  const before = answersByKey(previous);
  const after = answersByKey(current);
  const out = new Set<string>();
  for (const key of new Set([...before.keys(), ...after.keys()])) {
    if (
      answerFingerprint(before.get(key)) !== answerFingerprint(after.get(key))
    )
      out.add(key);
  }
  return out;
};

/**
 * Decisions to start the review from. Decisions already saved on this
 * submission win. Otherwise, on a resubmission, an answer the client left as
 * it was keeps the previous revision's acceptance; everything else (changed,
 * or previously sent back) waits for a fresh look.
 */
export const seedDecisions = (
  keys: string[],
  current: OutgoingIntakeSubmission,
  previous: OutgoingIntakeSubmission | null | undefined
): Record<string, DecisionState> => {
  const out: Record<string, DecisionState> = {};
  for (const k of keys) out[k] = { status: "pending", note: "" };
  const stored = current.decisions ?? [];
  if (stored.length) {
    for (const d of stored) {
      const k = decisionKey(d);
      if (out[k])
        out[k] =
          d.status === "rejected"
            ? { status: "rejected", note: d.note ?? "" }
            : { status: "accepted", note: "" };
    }
    return out;
  }
  if (!previous) return out;
  const changed = changedSince(current, previous) ?? new Set<string>();
  const prior = new Map<string, OutgoingIntakeReviewDecision>();
  for (const d of previous.decisions ?? []) prior.set(decisionKey(d), d);
  for (const k of keys) {
    if (!changed.has(k) && prior.get(k)?.status === "accepted")
      out[k] = { status: "accepted", note: "", carried: true };
  }
  return out;
};

/** The AI check's reason an upload needs a second look, or null. */
export const attentionReason = (
  answer: OutgoingIntakeAnswer | null | undefined
): string | null => {
  for (const f of answer?.files ?? []) {
    const warning = (f.reviewWarnings ?? []).find((w) => text(w.message));
    if (warning) return text(warning.message);
    if (f.reviewStatus === "failed" || f.reviewStatus === "warning") {
      return (
        text(f.readability?.message) ||
        text(f.fileIntegrity?.message) ||
        text(f.duplicate?.message) ||
        "The file couldn't be checked"
      );
    }
  }
  return null;
};

export type ReviewFilter =
  | "all"
  | "changed"
  | "attention"
  | "pending"
  | "flagged";

export const REVIEW_FILTERS: { id: ReviewFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "changed", label: "Changed" },
  { id: "attention", label: "Needs Attention" },
  { id: "pending", label: "Pending" },
  { id: "flagged", label: "Flagged" }
];

export type FilterableRow = {
  decision: Decision;
  changed: boolean;
  attention: string | null;
};

export const matchesFilter = (row: FilterableRow, filter: ReviewFilter) => {
  switch (filter) {
    case "changed":
      return row.changed;
    case "attention":
      return row.attention !== null;
    case "pending":
      return row.decision === "pending";
    case "flagged":
      return row.decision === "rejected";
    default:
      return true;
  }
};

/** Pending answers that are safe to accept in bulk: no AI warning on them. */
export const safeToAccept = <T extends FilterableRow & { key: string }>(
  rows: T[]
): string[] =>
  rows
    .filter((r) => r.decision === "pending" && r.attention === null)
    .map((r) => r.key);

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

/** "1 answer goes back" / "3 answers go back". */
export const goesBack = (n: number): string =>
  `${plural(n, "answer", "answers")} ${n === 1 ? "goes" : "go"} back`;

/**
 * The message at the top of the revision email. It doesn't repeat the
 * flagged questions: the email lists each one with its note.
 */
export const draftRevisionNote = (
  clientFirstName: string | null,
  flagged: { label: string; note: string }[]
): string => {
  const hi = clientFirstName ? `Hi ${clientFirstName}, ` : "Hi, ";
  if (flagged.length === 0) return "";
  const what =
    flagged.length === 1
      ? "one answer needs another look"
      : "a few answers need another look";
  return `${hi}${what}. Everything else looks good.`;
};
